enum LoadStatus {
    Unloaded,
    Loading,
    Loaded,
    LoadErrored,
    ReLoading,
}

export interface ILoadableUnloaded<T> {
    readonly $: LoadStatus.Unloaded;
}

export interface ILoadableLoading<T> {
    readonly $: LoadStatus.Loading;
}

export interface ILoadableLoaded<T> {
    readonly $: LoadStatus.Loaded;
    readonly data: T;
}

export interface ILoadableErrored<T> {
    readonly $: LoadStatus.LoadErrored;
    readonly error: Error;
}

export interface ILoadableReloading<T> {
    readonly $: LoadStatus.ReLoading;
    readonly data: T;
}

export type TLoadable<T>
    = ILoadableUnloaded<T>
    | ILoadableLoading<T>
    | ILoadableLoaded<T>
    | ILoadableErrored<T>
    | ILoadableReloading<T>;

// CHECK TYPE

export function isUnloaded<T>(d: TLoadable<T>): d is ILoadableUnloaded<T> {
    return d.$ === LoadStatus.Unloaded;
}

export function isLoading<T>(d: TLoadable<T>): d is ILoadableLoading<T> {
    return d.$ === LoadStatus.Loading;
}

export function isLoaded<T>(d: TLoadable<T>): d is ILoadableLoaded<T> {
    return d.$ === LoadStatus.Loaded || d.$ === LoadStatus.ReLoading;
}

export function isLoadErrored<T>(d: TLoadable<T>): d is ILoadableErrored<T> {
    return d.$ === LoadStatus.LoadErrored;
}

export function isReloading<T>(d: TLoadable<T>): d is ILoadableReloading<T> {
    return d.$ === LoadStatus.ReLoading;
}

// CREATE

export const createUnloaded = <T>(): ILoadableUnloaded<T> => ({ $: LoadStatus.Unloaded });

export const createLoading = <T>(): ILoadableLoading<T> => ({ $: LoadStatus.Loading });

export const createLoaded = <T>(data: T): ILoadableLoaded<T> => ({ $: LoadStatus.Loaded, data });

export const createLoadErrored = <T>(error: Error): ILoadableErrored<T> => ({ $: LoadStatus.LoadErrored, error });

export const createReloading = <T>(data: T): ILoadableReloading<T> => ({ $: LoadStatus.ReLoading, data });
